import React, { useCallback, useEffect, useMemo, useState } from "react";
import { auth, db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";
import { AlertTriangle, Bug, Clock3, RefreshCw, ShieldCheck } from "lucide-react";
import { postFunctionJson } from "../../utils/functionHttp";

const formatWhen = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
};

const pretty = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const toneClasses = {
  info: "bg-sky-50 text-sky-700 border-sky-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  error: "bg-rose-50 text-rose-700 border-rose-200",
  critical: "bg-red-50 text-red-700 border-red-200",
};

const Chip = ({ children, tone = "info" }) => (
  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${toneClasses[tone] || toneClasses.info}`}>
    {children}
  </span>
);

const JsonPreview = ({ value }) => {
  const content = useMemo(() => {
    try {
      return JSON.stringify(value || {}, null, 2);
    } catch (err) {
      return "{}";
    }
  }, [value]);

  if (!value || (typeof value === "object" && Object.keys(value).length === 0)) return null;

  return (
    <pre className="mt-2 overflow-x-auto rounded-xl bg-slate-950 px-3 py-2 text-[11px] leading-relaxed text-slate-100">
      {content}
    </pre>
  );
};

const DeveloperLogsTab = () => {
  const [companyId, setCompanyId] = useState("");
  const [auditLogs, setAuditLogs] = useState([]);
  const [errorLogs, setErrorLogs] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [loadingErrors, setLoadingErrors] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    const loadCompany = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;
        const snap = await getDoc(doc(db, "users", user.uid));
        if (!mounted || !snap.exists()) return;
        const data = snap.data() || {};
        setCompanyId(data.companyId || user.uid);
      } catch (err) {
        console.error("[DeveloperLogsTab] Failed to resolve companyId:", err);
      }
    };

    loadCompany();
    return () => {
      mounted = false;
    };
  }, []);

  const postSecure = useCallback(async (fnName, body) => {
    const user = auth.currentUser;
    if (!user) throw new Error("Not logged in");
    const authToken = await user.getIdToken(true);

    const res = await postFunctionJson(
      `https://us-central1-deliveryapp-c1bf2.cloudfunctions.net/${fnName}`,
      {
        authToken,
        body: {
          authToken,
          companyId,
          ...body,
        },
      }
    );

    const data = await res.json();
    if (!res.ok || data.success === false) {
      throw new Error(data.error || `Failed to load ${fnName}`);
    }

    return data;
  }, [companyId]);

  const loadAuditLogs = useCallback(async () => {
    if (!companyId) return;
    setLoadingAudit(true);
    setError("");
    try {
      const data = await postSecure("queryAuditLogsSecure", { limit: 75 });
      setAuditLogs(Array.isArray(data.logs) ? data.logs : []);
    } catch (err) {
      console.error("[DeveloperLogsTab] loadAuditLogs error:", err);
      setError(err.message || "Could not load audit logs.");
    } finally {
      setLoadingAudit(false);
    }
  }, [companyId, postSecure]);

  const loadErrorLogs = useCallback(async () => {
    if (!companyId) return;
    setLoadingErrors(true);
    setError("");
    try {
      const data = await postSecure("queryErrorLogs", { limit: 75 });
      setErrorLogs(Array.isArray(data.logs) ? data.logs : []);
    } catch (err) {
      console.error("[DeveloperLogsTab] loadErrorLogs error:", err);
      setError(err.message || "Could not load error logs.");
    } finally {
      setLoadingErrors(false);
    }
  }, [companyId, postSecure]);

  useEffect(() => {
    if (!companyId) return;
    loadAuditLogs();
    loadErrorLogs();
  }, [companyId, loadAuditLogs, loadErrorLogs]);

  const auditSummary = useMemo(() => {
    const critical = auditLogs.filter((log) => log.severity === "critical").length;
    const warnings = auditLogs.filter((log) => log.severity === "warning").length;
    return { total: auditLogs.length, critical, warnings };
  }, [auditLogs]);

  const errorSummary = useMemo(() => {
    const runtime = errorLogs.filter((log) => log.category === "runtime").length;
    const api = errorLogs.filter((log) => log.category === "api").length;
    return { total: errorLogs.length, runtime, api };
  }, [errorLogs]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-sky-600" />
                <h2 className="text-sm font-semibold text-slate-900">Audit Log</h2>
              </div>
              <p className="mt-1 text-xs text-slate-500">Tracked changes for accounts, inventory, users, and sample orders.</p>
            </div>
            <button
              type="button"
              onClick={loadAuditLogs}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
              disabled={loadingAudit}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loadingAudit ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-3">
            <div className="rounded-xl bg-slate-50 p-3">
              <div className="text-[11px] text-slate-500">Entries</div>
              <div className="mt-1 text-lg font-semibold text-slate-900">{auditSummary.total}</div>
            </div>
            <div className="rounded-xl bg-amber-50 p-3">
              <div className="text-[11px] text-amber-700">Warnings</div>
              <div className="mt-1 text-lg font-semibold text-amber-900">{auditSummary.warnings}</div>
            </div>
            <div className="rounded-xl bg-red-50 p-3">
              <div className="text-[11px] text-red-700">Critical</div>
              <div className="mt-1 text-lg font-semibold text-red-900">{auditSummary.critical}</div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Bug className="h-4 w-4 text-rose-600" />
                <h2 className="text-sm font-semibold text-slate-900">Error Log</h2>
              </div>
              <p className="mt-1 text-xs text-slate-500">Captured frontend/runtime errors and rejected promises from the app.</p>
            </div>
            <button
              type="button"
              onClick={loadErrorLogs}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
              disabled={loadingErrors}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loadingErrors ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-3">
            <div className="rounded-xl bg-slate-50 p-3">
              <div className="text-[11px] text-slate-500">Entries</div>
              <div className="mt-1 text-lg font-semibold text-slate-900">{errorSummary.total}</div>
            </div>
            <div className="rounded-xl bg-rose-50 p-3">
              <div className="text-[11px] text-rose-700">Runtime</div>
              <div className="mt-1 text-lg font-semibold text-rose-900">{errorSummary.runtime}</div>
            </div>
            <div className="rounded-xl bg-orange-50 p-3">
              <div className="text-[11px] text-orange-700">API / Promise</div>
              <div className="mt-1 text-lg font-semibold text-orange-900">{errorSummary.api}</div>
            </div>
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <Clock3 className="h-4 w-4 text-slate-500" />
            <h3 className="text-sm font-semibold text-slate-900">Recent Audit Activity</h3>
          </div>

          <div className="space-y-3">
            {auditLogs.length === 0 ? (
              <div className="rounded-xl bg-slate-50 px-3 py-4 text-sm text-slate-500">
                {loadingAudit ? "Loading audit logs…" : "No audit entries found yet."}
              </div>
            ) : (
              auditLogs.map((log) => (
                <div key={log.id} className="rounded-xl border border-slate-200 px-3 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Chip tone={log.severity || "info"}>{pretty(log.severity || "info")}</Chip>
                    <Chip>{pretty(log.entityType || "item")}</Chip>
                    <span className="text-xs font-medium text-slate-900">{pretty(log.action || "updated")}</span>
                  </div>
                  <div className="mt-2 text-sm text-slate-700">
                    {log.metadata?.accountName || log.metadata?.itemName || log.metadata?.userName || log.entityId || "Unknown target"}
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    {formatWhen(log.timestamp)} {log.userEmail ? `· ${log.userEmail}` : ""}
                  </div>
                  <JsonPreview value={log.metadata} />
                </div>
              ))
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-rose-500" />
            <h3 className="text-sm font-semibold text-slate-900">Recent Errors</h3>
          </div>

          <div className="space-y-3">
            {errorLogs.length === 0 ? (
              <div className="rounded-xl bg-slate-50 px-3 py-4 text-sm text-slate-500">
                {loadingErrors ? "Loading error logs…" : "No captured app errors found yet."}
              </div>
            ) : (
              errorLogs.map((log) => (
                <div key={log.id} className="rounded-xl border border-slate-200 px-3 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Chip tone={log.severity || "error"}>{pretty(log.severity || "error")}</Chip>
                    <Chip>{pretty(log.category || "runtime")}</Chip>
                    <Chip>{pretty(log.source || "client")}</Chip>
                  </div>
                  <div className="mt-2 text-sm font-medium text-slate-900">{log.message}</div>
                  <div className="mt-1 text-xs text-slate-500">
                    {formatWhen(log.timestamp)} {log.userEmail ? `· ${log.userEmail}` : ""}
                  </div>
                  <JsonPreview value={{ url: log.url, context: log.context, stack: log.stack }} />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeveloperLogsTab;
